import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { useRef, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import SignatureImageCropDialog from "./SignatureImageCropDialog"

export const ALLOWED_SIGNATURE_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]

const SIGNATURE_IMAGE_VALIDATION_MESSAGE =
  "Only JPEG, PNG, GIF, and WebP images are allowed. Video files are not supported."

function readImageAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error("Image upload failed."))
    reader.readAsDataURL(file)
  })
}

function validateSignatureImageFile(file) {
  if (!ALLOWED_SIGNATURE_IMAGE_TYPES.includes(file.type)) {
    throw new Error(SIGNATURE_IMAGE_VALIDATION_MESSAGE)
  }
  if (file.type.startsWith("video/")) {
    throw new Error(SIGNATURE_IMAGE_VALIDATION_MESSAGE)
  }
}

export default function SignatureImageUpload({
  disabled = false,
  onImageReady,
}) {
  const fileInputRef = useRef()
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [cropImageData, setCropImageData] = useState("")
  const [error, setError] = useState(null)

  function handleUploadButtonClicked() {
    fileInputRef.current?.click()
  }

  function handleFileInputChanged(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) {
      return
    }

    try {
      validateSignatureImageFile(file)
      setError(null)
    } catch (validationError) {
      setError(validationError.message)
      return
    }

    readImageAsDataUrl(file)
      .then((dataUrl) => {
        setCropImageData(dataUrl)
        setCropDialogOpen(true)
      })
      .catch(() => {
        setError("Image upload failed.")
      })
  }

  function handleCropFinished(croppedDataUrl) {
    onImageReady?.(croppedDataUrl)
    setCropDialogOpen(false)
    setCropImageData("")
  }

  function handleCropDialogClosed() {
    setCropDialogOpen(false)
    setCropImageData("")
  }

  return (
    <>
      <Column
        sx={{
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Row
          sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}
        >
          <Column sx={{ gap: 0.25, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Signature image
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload separately from the text editor. JPEG, PNG, GIF, or WebP
              only.
            </Typography>
          </Column>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            disabled={disabled}
            onClick={handleUploadButtonClicked}
            sx={{ flexShrink: 0 }}
          >
            Upload image
          </Button>
        </Row>
        {error ? (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_SIGNATURE_IMAGE_TYPES.join(",")}
          onChange={handleFileInputChanged}
          style={{ display: "none" }}
        />
      </Column>

      <SignatureImageCropDialog
        open={cropDialogOpen}
        imgData={cropImageData}
        onClose={handleCropDialogClosed}
        onCropFinished={handleCropFinished}
      />
    </>
  )
}
